'use client'

import {
    AiBillingMode,
    AiBillingPolicy,
    CashRegisterPolicy,
    CheckoutPaymentPolicy,
    CommissionBasis,
    InstantRefundPolicy,
    OfflineSettlementPolicy,
    ReceiptReviewPolicy,
    ReturnConsultationBillingPolicy,
    getAiBillingPolicy,
    getCashRegisterPolicy,
    getCheckoutPaymentPolicy,
    getCatalogCostPolicy,
    getCommissionBasisPolicy,
    getInstantRefundPolicy,
    getOfflineSettlementPolicy,
    getReceiptReviewPolicy,
    getReturnConsultationBillingPolicy,
    updateAiBillingPolicy,
    updateCashRegisterPolicy,
    updateCheckoutPaymentPolicy,
    updateCatalogCostPolicy,
    updateCommissionBasisPolicy,
    updateInstantRefundPolicy,
    updateOfflineSettlementPolicy,
    updateReceiptReviewPolicy,
    updateReturnConsultationBillingPolicy,
} from '@/lib/api/financial/financial-settings-api'
import {
    Setting,
    createSetting,
    getSettingByName,
    updateSetting,
} from '@/lib/api/utils/settings-api'
import { resetOfflineSettlementPolicyCache } from '@/lib/hooks/use-offline-settlement-policy'
import { resetReturnConsultationBillingPolicyCache } from '@/lib/hooks/use-return-consultation-billing-policy'
import { resetInstantRefundPolicyCache } from '@/lib/hooks/use-instant-refund-policy'
import { useAuth } from '@/store/hooks'
import { mask, unMask } from '@/lib/form-state/util'
import { useEffect, useState } from 'react'

/**
 * As quatro respostas para "a IA cobra no agendamento?".
 *
 * A ordem é do mais restritivo ao mais permissivo, e o texto explica a
 * CONSEQUÊNCIA — quem configura precisa saber que "Nunca cobrar" não impede a IA
 * de vender produto com link, e que "Desabilitar" impede.
 */
const AI_BILLING_OPTIONS: {
    mode: AiBillingMode
    label: string
    description: string
}[] = [
    {
        mode: 'DISABLED',
        label: 'Desabilitar a IA de gerar cobranças',
        description:
            'A IA nunca gera cobrança nem envia link de pagamento, em situação nenhuma — nem no agendamento, nem na venda de produtos. Ela informa o valor e diz que o pagamento é combinado com a equipe.',
    },
    {
        mode: 'ASK',
        label: 'Perguntar ao cliente se quer pagar agora ou no dia',
        description:
            'A IA informa o valor e pergunta quando o cliente prefere pagar. Escolhendo agora, ela envia o link; escolhendo no dia, o agendamento é confirmado sem cobrança.',
    },
    {
        mode: 'ALWAYS',
        label: 'Sempre cobrar no momento do agendamento',
        description:
            'A IA pergunta a forma de pagamento e envia o link antes de encerrar. É o comportamento padrão de quem não alterar esta opção.',
    },
    {
        mode: 'NEVER',
        label: 'Nunca cobrar no agendamento',
        description:
            'O cliente paga no dia do atendimento, na recepção. A IA informa o valor mas não pergunta forma de pagamento nem envia link. Continua podendo cobrar em venda de produtos.',
    },
]

/**
 * As duas respostas para "sobre o que a comissão percentual incide?".
 *
 * O exemplo no texto é o que faz a diferença ser entendida: numa venda em que a
 * maior parte é repasse, a mesma taxa paga valores muito diferentes.
 */
const COMMISSION_BASIS_OPTIONS: {
    basis: CommissionBasis
    label: string
    description: string
}[] = [
    {
        basis: 'REVENUE',
        label: 'Sobre a venda total',
        description:
            'O percentual incide sobre o valor cobrado do cliente. Numa venda de R$ 30.000,00, 10% de comissão são R$ 3.000,00, independente de quanto foi repassado.',
    },
    {
        basis: 'MARGIN',
        label: 'Sobre o markup',
        description:
            'O percentual incide sobre o que sobra para a empresa: receita líquida da taxa do gateway menos os repasses a parceiros, profissionais, hospitais e materiais. Numa venda de R$ 30.000,00 com R$ 25.000,00 de repasse, 10% de comissão são R$ 500,00. Atendimento com repasse não apurado fica sem comissão até ser apurado.',
    },
]

/** Config por empresa: markup mínimo aceito (%). Espelha o backend. */
const MIN_MARKUP_SETTING = 'min_markup_percentage'

/** Campo vazio, negativo ou não numérico = 0 (desligado). Aceita vírgula. */
const normalizeMinMarkupInput = (raw: string): number => {
    const parsed = parseFloat(raw.replace(',', '.'))
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0
}
import {
    MdArrowBack,
    MdCreditCard,
    MdDocumentScanner,
    MdPayments,
    MdPercent,
    MdPointOfSale,
    MdReceiptLong,
    MdSave,
    MdSmartToy,
    MdEventRepeat,
    MdReplay,
    MdTrendingUp,
    MdAccountBalance,
    MdSchedule,
    MdSell,
} from 'react-icons/md'
import { useNavigate, useSearchParams } from 'react-router-dom'
import FeesTab from './fees-tab'
import ProviderFeesTab from './provider-fees-tab'
import TabBranchActions from './tab-branch-actions'
import { toast } from 'react-toastify'

// Teto da operadora — a tabela de taxas dela vai até 21x. Espelha o
// MAX_INSTALLMENTS_HARD_LIMIT do backend, que recusa acima disso.
const MAX_INSTALLMENTS = 21

/**
 * Abas da tela. Agrupam por PERGUNTA que o operador está respondendo, não por
 * setting: "como o cliente paga" (checkout), "quanto sai de taxa", "como o
 * dinheiro que entra é conferido" e "qual markup eu aceito vender".
 */
type SettingsTabKey =
    'checkout' | 'fees' | 'providerFees' | 'receiving' | 'pricing'

const SETTINGS_TABS: {
    key: SettingsTabKey
    label: string
    icon: typeof MdPayments
}[] = [
    { key: 'checkout', label: 'Cobrança', icon: MdPayments },
    { key: 'fees', label: 'Taxas', icon: MdPercent },
    { key: 'providerFees', label: 'Taxas Fixas', icon: MdAccountBalance },
    { key: 'receiving', label: 'Recebimentos', icon: MdPointOfSale },
    { key: 'pricing', label: 'Precificação', icon: MdTrendingUp },
]

const DEFAULT_TAB: SettingsTabKey = 'checkout'

const parseTab = (raw: string | null): SettingsTabKey =>
    SETTINGS_TABS.some(t => t.key === raw)
        ? (raw as SettingsTabKey)
        : DEFAULT_TAB

const currencyFormat = (value: string | number | null | undefined): string => {
    if (value === null || value === undefined) value = '0'
    value = `${value}`.replace(/[^0-9]+/g, '')
    return mask(value, '', 'currency', {
        prefix: 'R$ ',
        decimalSeparator: ',',
        groupSeparator: '.',
        precision: 2,
    })
}

const parseCurrencyToCents = (value: string): number => {
    if (!value.trim()) return 0
    return parseInt(unMask(value, 'currency')) || 0
}

const formatCentsToDisplay = (cents: number | null | undefined): string => {
    if (!cents) return ''
    return currencyFormat(cents.toString())
}

/** Estado de edição da política de meios de pagamento e parcelamento. */
interface PaymentPolicyState {
    methods: { pix: boolean; creditCard: boolean; debitCard: boolean }
    installmentsEnabled: boolean
    maxInstallments: number
    /** Valor mínimo por parcela mascarado em R$. */
    minInstallmentValue: string
    /** Mapa parcela -> valor mínimo total mascarado em R$. */
    tiers: Record<number, string>
}

const paymentToState = (p: CheckoutPaymentPolicy): PaymentPolicyState => ({
    methods: {
        pix: p.methods?.pix ?? true,
        creditCard: p.methods?.creditCard ?? true,
        debitCard: p.methods?.debitCard ?? true,
    },
    installmentsEnabled: p.installments?.enabled ?? true,
    maxInstallments: p.installments?.maxInstallments || MAX_INSTALLMENTS,
    minInstallmentValue: formatCentsToDisplay(
        p.installments?.minInstallmentValue
    ),
    tiers: (p.installments?.tiers || []).reduce(
        (acc, t) => {
            acc[t.count] = formatCentsToDisplay(t.minAmount)
            return acc
        },
        {} as Record<number, string>
    ),
})

const buildPaymentPolicy = (s: PaymentPolicyState): CheckoutPaymentPolicy => {
    const tiers = Object.entries(s.tiers)
        .map(([count, display]) => ({
            count: Number(count),
            minAmount: parseCurrencyToCents(display),
        }))
        .filter(
            t => t.count >= 2 && t.count <= s.maxInstallments && t.minAmount > 0
        )
    return {
        methods: { ...s.methods },
        installments: {
            enabled: s.installmentsEnabled,
            maxInstallments: s.maxInstallments,
            minInstallmentValue: parseCurrencyToCents(s.minInstallmentValue),
            tiers,
        },
    }
}

export default function FinancialSettingsPage() {
    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams()
    const activeTab = parseTab(searchParams.get('tab'))
    const setActiveTab = (tab: SettingsTabKey) => {
        setSearchParams(
            prev => {
                const params = new URLSearchParams(prev)
                if (tab === DEFAULT_TAB) params.delete('tab')
                else params.set('tab', tab)
                return params
            },
            { replace: true }
        )
    }
    const { user } = useAuth()
    /**
     * A cobrança pela IA no agendamento só faz sentido na SAÚDE: as opções falam
     * de consulta, exame e teleconsulta, e é lá que existe o "paga no dia, na
     * recepção". Fora do segmento a IA não agenda esses serviços.
     */
    const isHealthSegment = user?.company?.segment === 'HEALTH'
    /**
     * A cobrança do RETORNO só existe onde existe retorno: a saúde que atende o
     * próprio paciente. A intermediação revende o atendimento de um parceiro e
     * cobra por encaminhamento — ali não há "volta para mostrar o exame", e a
     * consulta de retorno nem é marcada nesta operação.
     */
    const showReturnConsultation =
        isHealthSegment &&
        user?.company?.secondarySegment !== 'INTERMEDIATION'

    const [minMarkup, setMinMarkup] = useState('')
    const [minMarkupSetting, setMinMarkupSetting] = useState<Setting | null>(
        null
    )
    const [savingMarkup, setSavingMarkup] = useState(false)

    // Base da comissão percentual por produtividade
    const [commissionBasis, setCommissionBasis] =
        useState<CommissionBasis>('REVENUE')
    const [savingCommissionBasis, setSavingCommissionBasis] = useState(false)

    // Item do catálogo com venda e custo zero
    const [allowZeroCost, setAllowZeroCost] = useState(false)
    const [savingCatalogCost, setSavingCatalogCost] = useState(false)

    const [loading, setLoading] = useState(true)

    // Meios de pagamento e parcelamento (checkout)
    const [savingPayment, setSavingPayment] = useState(false)
    const [payment, setPayment] = useState<PaymentPolicyState>({
        methods: { pix: true, creditCard: true, debitCard: true },
        installmentsEnabled: true,
        maxInstallments: MAX_INSTALLMENTS,
        minInstallmentValue: '',
        tiers: {},
    })

    // Cobrança pela IA no agendamento
    const [savingAiBilling, setSavingAiBilling] = useState(false)
    const [aiBilling, setAiBilling] = useState<AiBillingPolicy>({
        mode: 'ALWAYS',
    })

    // Cobrança da consulta de retorno
    const [savingReturnConsultation, setSavingReturnConsultation] =
        useState(false)
    const [returnConsultation, setReturnConsultation] =
        useState<ReturnConsultationBillingPolicy>({ chargeReturn: false })

    // Conferência de comprovantes (leitura por IA)
    const [savingReceipt, setSavingReceipt] = useState(false)
    const [receipt, setReceipt] = useState<ReceiptReviewPolicy>({
        autoApprove: false,
        minTrustScore: 0.85,
    })

    // Estorno sem passar pela fila de aprovação
    const [savingInstantRefund, setSavingInstantRefund] = useState(false)
    const [instantRefund, setInstantRefund] = useState<InstantRefundPolicy>({
        allowInstantRefund: false,
    })

    // Horário de fechamento do caixa diário
    const [savingCashRegister, setSavingCashRegister] = useState(false)
    const [cashRegister, setCashRegister] = useState<CashRegisterPolicy>({
        closingTime: '18:00',
    })

    // Baixa de pagamentos recebidos por fora do sistema
    const [savingOffline, setSavingOffline] = useState(false)
    const [offline, setOffline] = useState<OfflineSettlementPolicy>({
        autoSettle: false,
    })

    useEffect(() => {
        load()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.companyId])

    const load = async () => {
        setLoading(true)
        try {
            const [
                payRes,
                receiptRes,
                offlineRes,
                aiBillingRes,
                returnConsultationRes,
                instantRefundRes,
                commissionBasisRes,
                cashRegisterRes,
                catalogCostRes,
            ] = await Promise.all([
                getCheckoutPaymentPolicy(),
                getReceiptReviewPolicy(),
                getOfflineSettlementPolicy(),
                isHealthSegment ? getAiBillingPolicy() : null,
                showReturnConsultation
                    ? getReturnConsultationBillingPolicy()
                    : null,
                getInstantRefundPolicy(),
                getCommissionBasisPolicy(),
                getCashRegisterPolicy(),
                getCatalogCostPolicy(),
            ])
            if (payRes.success) {
                setPayment(paymentToState(payRes.result))
            }
            if (aiBillingRes?.success) {
                setAiBilling(aiBillingRes.result)
            }
            if (returnConsultationRes?.success) {
                setReturnConsultation(returnConsultationRes.result)
            }
            if (receiptRes.success) {
                setReceipt(receiptRes.result)
            }
            if (offlineRes.success) {
                setOffline(offlineRes.result)
            }
            if (instantRefundRes.success) {
                setInstantRefund(instantRefundRes.result)
            }
            if (cashRegisterRes.success) {
                setCashRegister(cashRegisterRes.result)
            }
            if (commissionBasisRes.success) {
                setCommissionBasis(commissionBasisRes.result.basis)
            }
            if (catalogCostRes.success) {
                setAllowZeroCost(catalogCostRes.result.allowZeroCost)
            }
            if (user?.companyId) {
                const markupRes = await getSettingByName(
                    MIN_MARKUP_SETTING,
                    user.companyId
                )
                if (markupRes.success && markupRes.result) {
                    setMinMarkupSetting(markupRes.result)
                    setMinMarkup(markupRes.result.value || '')
                }
            }
        } catch {
            toast.error('Erro ao carregar configurações financeiras')
        } finally {
            setLoading(false)
        }
    }

    const handleSaveMarkup = async () => {
        if (!user?.companyId) return
        const normalized = normalizeMinMarkupInput(minMarkup)
        setMinMarkup(normalized ? String(normalized) : '')

        setSavingMarkup(true)
        try {
            const res = minMarkupSetting
                ? await updateSetting(minMarkupSetting.id, {
                      value: String(normalized),
                  })
                : await createSetting({
                      name: MIN_MARKUP_SETTING,
                      value: String(normalized),
                      companyId: user.companyId,
                      role: 'COMPANY',
                  })
            if (res.success) {
                setMinMarkupSetting(res.result!)
                toast.success('Markup mínimo salvo com sucesso')
            } else {
                toast.error('Erro ao salvar o markup mínimo')
            }
        } catch {
            toast.error('Erro ao salvar o markup mínimo')
        } finally {
            setSavingMarkup(false)
        }
    }

    const handleSaveCommissionBasis = async () => {
        setSavingCommissionBasis(true)
        try {
            const res = await updateCommissionBasisPolicy({
                basis: commissionBasis,
            })
            if (res.success) {
                toast.success('Base da comissão salva com sucesso')
                setCommissionBasis(res.result.basis)
            } else {
                toast.error(res.error || 'Erro ao salvar a base da comissão')
            }
        } catch {
            toast.error('Erro ao salvar a base da comissão')
        } finally {
            setSavingCommissionBasis(false)
        }
    }

    const handleSaveCatalogCost = async () => {
        setSavingCatalogCost(true)
        try {
            const res = await updateCatalogCostPolicy({ allowZeroCost })
            if (res.success) {
                toast.success('Custo do item do catálogo salvo com sucesso')
                setAllowZeroCost(res.result.allowZeroCost)
            } else {
                toast.error(
                    res.error || 'Erro ao salvar o custo do item do catálogo'
                )
            }
        } catch {
            toast.error('Erro ao salvar o custo do item do catálogo')
        } finally {
            setSavingCatalogCost(false)
        }
    }

    const handleSaveReturnConsultation = async () => {
        setSavingReturnConsultation(true)
        try {
            const res =
                await updateReturnConsultationBillingPolicy(returnConsultation)
            if (res.success) {
                toast.success('Cobrança da consulta de retorno salva com sucesso')
                setReturnConsultation(res.result)
                // O formulário de agendamento lê a política de um cache de sessão
                // — sem isto, o operador salvaria aqui e o próximo retorno ainda
                // nasceria com a regra antiga até dar refresh.
                resetReturnConsultationBillingPolicyCache()
            } else {
                toast.error(
                    res.error ||
                        'Erro ao salvar a cobrança da consulta de retorno'
                )
            }
        } catch {
            toast.error('Erro ao salvar a cobrança da consulta de retorno')
        } finally {
            setSavingReturnConsultation(false)
        }
    }

    const handleSaveReceipt = async () => {
        setSavingReceipt(true)
        try {
            const res = await updateReceiptReviewPolicy(receipt)
            if (res.success) {
                toast.success('Conferência de comprovantes salva com sucesso')
                setReceipt(res.result)
            } else {
                toast.error(
                    res.error || 'Erro ao salvar conferência de comprovantes'
                )
            }
        } catch {
            toast.error('Erro ao salvar conferência de comprovantes')
        } finally {
            setSavingReceipt(false)
        }
    }

    const handleSaveOffline = async () => {
        setSavingOffline(true)
        try {
            const res = await updateOfflineSettlementPolicy(offline)
            if (res.success) {
                toast.success('Baixa de pagamentos por fora salva com sucesso')
                setOffline(res.result)
                // As telas de cobrança leem a política de um cache de sessão —
                // sem isto, o operador salvaria aqui e continuaria vendo o botão
                // bloqueado até dar refresh.
                resetOfflineSettlementPolicyCache()
            } else {
                toast.error(
                    res.error || 'Erro ao salvar a baixa de pagamentos por fora'
                )
            }
        } catch {
            toast.error('Erro ao salvar a baixa de pagamentos por fora')
        } finally {
            setSavingOffline(false)
        }
    }

    const handleSaveInstantRefund = async () => {
        setSavingInstantRefund(true)
        try {
            const res = await updateInstantRefundPolicy(instantRefund)
            if (res.success) {
                toast.success('Regra de estorno salva com sucesso')
                setInstantRefund(res.result)
                // As telas de cobrança leem a política de um cache de sessão — sem
                // isto, o operador salvaria aqui e continuaria abrindo solicitação
                // até dar refresh.
                resetInstantRefundPolicyCache()
            } else {
                toast.error(res.error || 'Erro ao salvar a regra de estorno')
            }
        } catch {
            toast.error('Erro ao salvar a regra de estorno')
        } finally {
            setSavingInstantRefund(false)
        }
    }

    const handleSaveCashRegister = async () => {
        if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(cashRegister.closingTime)) {
            toast.error('Informe um horário válido (HH:MM)')
            return
        }
        setSavingCashRegister(true)
        try {
            const res = await updateCashRegisterPolicy(cashRegister)
            if (res.success) {
                toast.success('Horário de fechamento do caixa salvo com sucesso')
                setCashRegister(res.result)
            } else {
                toast.error(
                    res.error || 'Erro ao salvar o horário de fechamento do caixa'
                )
            }
        } catch {
            toast.error('Erro ao salvar o horário de fechamento do caixa')
        } finally {
            setSavingCashRegister(false)
        }
    }

    const handleSavePayment = async () => {
        setSavingPayment(true)
        try {
            const res = await updateCheckoutPaymentPolicy(
                buildPaymentPolicy(payment)
            )
            if (res.success) {
                toast.success('Meios de pagamento salvos com sucesso')
                setPayment(paymentToState(res.result))
            } else {
                toast.error(res.error || 'Erro ao salvar meios de pagamento')
            }
        } catch {
            toast.error('Erro ao salvar meios de pagamento')
        } finally {
            setSavingPayment(false)
        }
    }

    const handleSaveAiBilling = async () => {
        setSavingAiBilling(true)
        try {
            const res = await updateAiBillingPolicy(aiBilling)
            if (res.success) {
                toast.success('Cobrança pela IA salva com sucesso')
                setAiBilling(res.result)
            } else {
                toast.error(res.error || 'Erro ao salvar cobrança pela IA')
            }
        } catch {
            toast.error('Erro ao salvar cobrança pela IA')
        } finally {
            setSavingAiBilling(false)
        }
    }

    const updateTier = (count: number, value: string) => {
        setPayment(prev => ({
            ...prev,
            tiers: { ...prev.tiers, [count]: currencyFormat(value) },
        }))
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <span className="loading loading-spinner loading-lg text-secondary" />
            </div>
        )
    }

    return (
        <div className="max-w-3xl mx-auto p-6 space-y-6">
            {/* Header */}
            <div className="flex items-center gap-4">
                <button
                    onClick={() => navigate(-1)}
                    className="btn btn-ghost btn-sm"
                >
                    <MdArrowBack className="text-lg" />
                </button>
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center">
                        <MdReceiptLong className="text-xl text-secondary" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold">
                            Configurações Financeiras
                        </h1>
                        <p className="text-sm text-base-content/60">
                            Parâmetros aplicados às cobranças geradas
                        </p>
                    </div>
                </div>
            </div>

            {/* A aba fica na URL: o operador que abre "Taxas", sai para conferir
                uma cobrança e volta pelo histórico cai na mesma aba, e o link é
                compartilhável com o colega. */}
            <div role="tablist" className="tabs tabs-bordered">
                {SETTINGS_TABS.map(tab => (
                    <button
                        key={tab.key}
                        role="tab"
                        type="button"
                        className={`tab gap-2 ${activeTab === tab.key ? 'tab-active font-semibold' : ''}`}
                        onClick={() => setActiveTab(tab.key)}
                    >
                        <tab.icon className="text-base" />
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Custo do provedor: leitura da API, sem nada para salvar — por isso
                mora num componente próprio em vez de mais um card aqui. */}
            {activeTab === 'providerFees' && <ProviderFeesTab />}

            {activeTab === 'pricing' && (
                <>
                    <TabBranchActions
                        scope="pricing"
                        label="Precificação"
                        disabled={savingMarkup || savingCommissionBasis}
                        buildPayload={() => ({
                            minMarkupPercentage: normalizeMinMarkupInput(minMarkup),
                            commissionBasis: { basis: commissionBasis },
                        })}
                        onImported={payload => {
                            setMinMarkup(
                                payload.minMarkupPercentage
                                    ? String(payload.minMarkupPercentage)
                                    : ''
                            )
                            if (payload.commissionBasis)
                                setCommissionBasis(payload.commissionBasis.basis)
                        }}
                    />
                    {/* Card: Markup mínimo aceito */}
                    <div className="card bg-base-100 shadow-sm border border-base-300">
                        <div className="card-body">
                            <div className="flex items-center gap-2 mb-1">
                                <MdPercent className="text-xl text-secondary" />
                                <h2 className="card-title text-lg">
                                    Markup mínimo aceito
                                </h2>
                            </div>
                            <p className="text-sm text-base-content/70 mb-4">
                                Bloqueia agendamentos e orçamentos de exames,
                                consultas e procedimentos cujo markup fique
                                abaixo deste percentual. O markup é calculado
                                sobre o custo: (venda − custo) ÷ custo. Deixe em
                                branco ou 0 para desligar.
                            </p>
                            <div className="flex flex-wrap items-end gap-4">
                                <div className="form-control">
                                    <label className="label py-1">
                                        <span className="label-text text-sm font-medium">
                                            Markup mínimo (%)
                                        </span>
                                    </label>
                                    <label className="input input-bordered flex items-center gap-2 w-40">
                                        <input
                                            type="number"
                                            min={0}
                                            step="0.1"
                                            placeholder="0"
                                            className="grow"
                                            value={minMarkup}
                                            onChange={e =>
                                                setMinMarkup(e.target.value)
                                            }
                                        />
                                        <span className="text-base-content/50">
                                            %
                                        </span>
                                    </label>
                                </div>
                                <div className="card-actions">
                                    <button
                                        onClick={handleSaveMarkup}
                                        disabled={savingMarkup}
                                        className="btn btn-secondary gap-2"
                                    >
                                        {savingMarkup ? (
                                            <span className="loading loading-spinner loading-sm" />
                                        ) : (
                                            <MdSave className="text-lg" />
                                        )}
                                        Salvar
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Card: Base da comissão percentual. Fica em Precificação porque
                        a pergunta é a mesma do markup mínimo: quanto da venda é da
                        empresa. O valor fixo por atendimento não depende dela. */}
                    <div className="card bg-base-100 shadow-sm border border-base-300">
                        <div className="card-body">
                            <div className="flex items-center gap-2 mb-1">
                                <MdPercent className="text-xl text-secondary" />
                                <h2 className="card-title text-lg">
                                    Base da comissão percentual
                                </h2>
                            </div>
                            <p className="text-sm text-base-content/70 mb-4">
                                Sobre o que incide a comissão por produtividade
                                cadastrada como percentual no perfil do usuário.
                                Comissão em valor fixo por atendimento não muda.
                            </p>

                            <div className="flex flex-col gap-3">
                                {COMMISSION_BASIS_OPTIONS.map(option => (
                                    <label
                                        key={option.basis}
                                        className={`flex items-start gap-3 cursor-pointer rounded-xl border p-4 transition-colors ${
                                            commissionBasis === option.basis
                                                ? 'border-secondary bg-secondary/5'
                                                : 'border-base-300 hover:bg-base-200/40'
                                        }`}
                                    >
                                        <input
                                            type="radio"
                                            name="commission-basis"
                                            className="radio radio-secondary mt-0.5"
                                            checked={commissionBasis === option.basis}
                                            onChange={() =>
                                                setCommissionBasis(option.basis)
                                            }
                                        />
                                        <span>
                                            <span className="font-medium block">
                                                {option.label}
                                            </span>
                                            <span className="text-sm text-base-content/60">
                                                {option.description}
                                            </span>
                                        </span>
                                    </label>
                                ))}
                            </div>

                            <div className="card-actions justify-end mt-6">
                                <button
                                    onClick={handleSaveCommissionBasis}
                                    disabled={savingCommissionBasis}
                                    className="btn btn-secondary gap-2"
                                >
                                    {savingCommissionBasis ? (
                                        <span className="loading loading-spinner loading-sm" />
                                    ) : (
                                        <MdSave className="text-lg" />
                                    )}
                                    Salvar
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Card: Custo do item do catálogo. Fica em Precificação porque
                        sem custo não há markup: o markup mínimo deixa de conferir o
                        item, e a margem dele some dos relatórios. É da ORGANIZAÇÃO,
                        não da filial — por isso fica fora do "Aplicar em todas". */}
                    <div className="card bg-base-100 shadow-sm border border-base-300">
                        <div className="card-body">
                            <div className="flex items-center gap-2 mb-1">
                                <MdSell className="text-xl text-secondary" />
                                <h2 className="card-title text-lg">
                                    Custo do item do catálogo
                                </h2>
                                <span className="badge badge-ghost badge-sm">
                                    Todas as filiais
                                </span>
                            </div>
                            <p className="text-sm text-base-content/70 mb-4">
                                Por padrão, item do catálogo com preço de venda
                                precisa de preço de custo: sem ele não há markup
                                nem margem para conferir. O custo do vínculo com
                                parceiro ou profissional continua obrigatório,
                                porque é dele que sai o repasse. Esta opção vale
                                para todas as filiais da organização, já que o
                                catálogo e o preço replicado entre filiais são
                                os mesmos.
                            </p>

                            <label className="flex items-start gap-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    className="checkbox checkbox-secondary mt-0.5"
                                    checked={allowZeroCost}
                                    onChange={e =>
                                        setAllowZeroCost(e.target.checked)
                                    }
                                />
                                <span>
                                    <span className="font-medium block">
                                        Permitir cadastrar item sem custo
                                    </span>
                                    <span className="text-sm text-base-content/60">
                                        O item pode ser salvo com preço de venda e
                                        custo zero, para o que não tem custo
                                        próprio (serviço feito pela própria
                                        equipe, taxa, brinde). Esses itens ficam
                                        fora da conferência de markup mínimo.
                                    </span>
                                </span>
                            </label>

                            <div className="card-actions justify-end mt-6">
                                <button
                                    onClick={handleSaveCatalogCost}
                                    disabled={savingCatalogCost}
                                    className="btn btn-secondary gap-2"
                                >
                                    {savingCatalogCost ? (
                                        <span className="loading loading-spinner loading-sm" />
                                    ) : (
                                        <MdSave className="text-lg" />
                                    )}
                                    Salvar
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {activeTab === 'receiving' && (
                <>
                    <TabBranchActions
                        scope="receiving"
                        label="Recebimentos"
                        disabled={
                            savingReceipt ||
                            savingOffline ||
                            savingInstantRefund ||
                            savingCashRegister
                        }
                        buildPayload={() => ({
                            receipt,
                            offline,
                            instantRefund,
                            cashRegister,
                        })}
                        onImported={payload => {
                            if (payload.receipt) setReceipt(payload.receipt)
                            if (payload.offline) setOffline(payload.offline)
                            if (payload.instantRefund)
                                setInstantRefund(payload.instantRefund)
                            if (payload.cashRegister)
                                setCashRegister(payload.cashRegister)
                        }}
                    />
                    {/* Card: Conferência de comprovantes */}
                    <div className="card bg-base-100 shadow-sm border border-base-300">
                        <div className="card-body">
                            <div className="flex items-center gap-2 mb-1">
                                <MdDocumentScanner className="text-xl text-secondary" />
                                <h2 className="card-title text-lg">
                                    Conferência de comprovantes
                                </h2>
                            </div>
                            <p className="text-sm text-base-content/70 mb-4">
                                Ao anexar um comprovante em uma cobrança
                                pendente, a baixa entra na fila de aprovação
                                financeira, o sistema lê os comprovantes da fila
                                e confere valor, instituição emissora,
                                identificador da transação e sinais de
                                adulteração.
                            </p>

                            <label className="flex items-start gap-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    className="checkbox checkbox-secondary mt-0.5"
                                    checked={receipt.autoApprove}
                                    onChange={e =>
                                        setReceipt(prev => ({
                                            ...prev,
                                            autoApprove: e.target.checked,
                                        }))
                                    }
                                />
                                <span>
                                    <span className="font-medium block">
                                        Aprovar a baixa automaticamente quando o
                                        sistema validar o comprovante
                                    </span>
                                    <span className="text-sm text-base-content/60">
                                        A cobrança é marcada como paga e
                                        conciliada sem intervenção humana —
                                        apenas quando o valor bate exatamente e
                                        nenhum indício de fraude é encontrado.
                                        Comprovantes duvidosos continuam na fila
                                        para um operador decidir.
                                    </span>
                                </span>
                            </label>

                            {receipt.autoApprove && (
                                <div className="mt-6 p-4 rounded-xl border border-base-300 bg-base-50">
                                    <label className="label py-1">
                                        <span className="label-text font-medium">
                                            Confiança mínima para aprovar
                                            sozinha
                                        </span>
                                        <span className="label-text-alt font-semibold text-secondary">
                                            {Math.round(
                                                receipt.minTrustScore * 100
                                            )}
                                            %
                                        </span>
                                    </label>
                                    <input
                                        type="range"
                                        min={50}
                                        max={100}
                                        step={5}
                                        value={Math.round(
                                            receipt.minTrustScore * 100
                                        )}
                                        onChange={e =>
                                            setReceipt(prev => ({
                                                ...prev,
                                                minTrustScore:
                                                    Number(e.target.value) /
                                                    100,
                                            }))
                                        }
                                        className="range range-secondary range-sm"
                                    />
                                    <p className="text-xs text-base-content/50 mt-2">
                                        Quanto maior, mais rigorosa é a
                                        aprovação automática — e mais
                                        comprovantes caem na conferência humana.
                                    </p>
                                </div>
                            )}

                            <div className="card-actions justify-end mt-6">
                                <button
                                    onClick={handleSaveReceipt}
                                    disabled={savingReceipt}
                                    className="btn btn-secondary gap-2"
                                >
                                    {savingReceipt ? (
                                        <span className="loading loading-spinner loading-sm" />
                                    ) : (
                                        <MdSave className="text-lg" />
                                    )}
                                    Salvar conferência
                                </button>
                            </div>
                        </div>
                    </div>
                    {/* Card: Pagamentos recebidos por fora do sistema */}
                    <div className="card bg-base-100 shadow-sm border border-base-300">
                        <div className="card-body">
                            <div className="flex items-center gap-2 mb-1">
                                <MdPointOfSale className="text-xl text-secondary" />
                                <h2 className="card-title text-lg">
                                    Pagamentos recebidos por fora do sistema
                                </h2>
                            </div>
                            <p className="text-sm text-base-content/70 mb-4">
                                Cobranças em dinheiro, maquininha e "outro" são
                                recebidas fora do gateway: não existe webhook
                                nem extrato para o sistema conferir depois, e
                                quem recebeu costuma ser quem registra a baixa.
                            </p>

                            <label className="flex items-start gap-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    className="checkbox checkbox-secondary mt-0.5"
                                    checked={offline.autoSettle}
                                    onChange={e =>
                                        setOffline({
                                            autoSettle: e.target.checked,
                                        })
                                    }
                                />
                                <span>
                                    <span className="font-medium block">
                                        Permitir cobranças por fora do sistema
                                        serem automaticamente marcadas como pago
                                    </span>
                                    <span className="text-sm text-base-content/60">
                                        Vale para dinheiro, maquininha e outro.
                                        A cobrança nasce paga — e as que nascem
                                        sem método são baixadas assim que um
                                        desses for escolhido na edição. Sem
                                        comprovante e sem fila de aprovação.
                                        Contas a pagar e os demais métodos não
                                        mudam.
                                    </span>
                                </span>
                            </label>

                            <div className="card-actions justify-end mt-6">
                                <button
                                    onClick={handleSaveOffline}
                                    disabled={savingOffline}
                                    className="btn btn-secondary gap-2"
                                >
                                    {savingOffline ? (
                                        <span className="loading loading-spinner loading-sm" />
                                    ) : (
                                        <MdSave className="text-lg" />
                                    )}
                                    Salvar
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Card: Estorno sem aprovação. Fica aqui, e não em "Cobrança",
                        porque responde a mesma pergunta do card acima: o que passa
                        pela fila do financeiro e o que a equipe resolve na hora. */}
                    <div className="card bg-base-100 shadow-sm border border-base-300">
                        <div className="card-body">
                            <div className="flex items-center gap-2 mb-1">
                                <MdReplay className="text-xl text-secondary" />
                                <h2 className="card-title text-lg">
                                    Estorno sem aprovação
                                </h2>
                            </div>
                            <p className="text-sm text-base-content/70 mb-4">
                                Devolver dinheiro ao cliente normalmente vira uma
                                solicitação que um operador financeiro aprova com
                                senha. Quem resolve no balcão — o cliente desiste
                                da forma de pagamento e o dinheiro volta na hora —
                                fica com a solicitação parada esperando alguém
                                registrar o que já aconteceu.
                            </p>

                            <label className="flex items-start gap-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    className="checkbox checkbox-secondary mt-0.5"
                                    checked={instantRefund.allowInstantRefund}
                                    onChange={e =>
                                        setInstantRefund({
                                            allowInstantRefund:
                                                e.target.checked,
                                        })
                                    }
                                />
                                <span>
                                    <span className="font-medium block">
                                        Permitir estorno na hora, sem passar pela
                                        fila de aprovação
                                    </span>
                                    <span className="text-sm text-base-content/60">
                                        Quem tem permissão de estornar devolve o
                                        dinheiro no mesmo gesto, confirmando com a
                                        própria senha. O estorno continua
                                        registrado no histórico de aprovações, com
                                        recibo. As recusas de sempre continuam
                                        valendo: convênio, saldo já devolvido e
                                        repasse ao parceiro já pago.
                                    </span>
                                </span>
                            </label>

                            <div className="card-actions justify-end mt-6">
                                <button
                                    onClick={handleSaveInstantRefund}
                                    disabled={savingInstantRefund}
                                    className="btn btn-secondary gap-2"
                                >
                                    {savingInstantRefund ? (
                                        <span className="loading loading-spinner loading-sm" />
                                    ) : (
                                        <MdSave className="text-lg" />
                                    )}
                                    Salvar
                                </button>
                            </div>
                        </div>
                    </div>
                    {/* Card: Registro de caixa. Em "Recebimentos" porque decide em
                        que dia o dinheiro que entra é contado. */}
                    <div className="card bg-base-100 shadow-sm border border-base-300">
                        <div className="card-body">
                            <div className="flex items-center gap-2 mb-1">
                                <MdSchedule className="text-xl text-secondary" />
                                <h2 className="card-title text-lg">
                                    Fechamento do caixa
                                </h2>
                            </div>
                            <p className="text-sm text-base-content/70 mb-4">
                                O registro de caixa é diário. Tudo o que entrar a
                                partir do horário de fechamento conta para o caixa
                                do dia seguinte. A mudança vale para os
                                lançamentos daqui em diante: dias já registrados
                                não são recalculados.
                            </p>

                            <div className="form-control w-full max-w-xs">
                                <label
                                    className="label pb-1"
                                    htmlFor="cash-register-closing-time"
                                >
                                    <span className="label-text font-medium">
                                        Horário de fechamento
                                    </span>
                                </label>
                                <input
                                    id="cash-register-closing-time"
                                    type="time"
                                    className="input input-bordered w-full"
                                    value={cashRegister.closingTime}
                                    onChange={e =>
                                        setCashRegister({
                                            closingTime: e.target.value,
                                        })
                                    }
                                />
                                <span className="text-xs text-base-content/60 mt-2">
                                    Use 00:00 para fechar o caixa à meia-noite
                                    (sem corte).
                                </span>
                            </div>

                            <div className="card-actions justify-end mt-6">
                                <button
                                    onClick={handleSaveCashRegister}
                                    disabled={savingCashRegister}
                                    className="btn btn-secondary gap-2"
                                >
                                    {savingCashRegister ? (
                                        <span className="loading loading-spinner loading-sm" />
                                    ) : (
                                        <MdSave className="text-lg" />
                                    )}
                                    Salvar
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* Taxas praticadas: faixas por valor e por parcela, com base propria ou
                espelhando as taxas fixas. Mora num componente proprio — o formulario e grande
                e a pagina ja tem quatro outras secoes. */}
            {activeTab === 'fees' && <FeesTab />}

            {activeTab === 'checkout' && (
                <>
                    <TabBranchActions
                        scope="checkout"
                        label="Cobrança"
                        disabled={
                            savingPayment ||
                            savingAiBilling ||
                            savingReturnConsultation
                        }
                        buildPayload={() => ({
                            payment: buildPaymentPolicy(payment),
                            // Fora da saúde a política nem é carregada, e mandar
                            // o default do estado sobrescreveria a filial destino.
                            ...(isHealthSegment ? { aiBilling } : {}),
                            ...(showReturnConsultation
                                ? { returnConsultation }
                                : {}),
                        })}
                        onImported={payload => {
                            if (payload.payment)
                                setPayment(paymentToState(payload.payment))
                            if (payload.aiBilling)
                                setAiBilling(payload.aiBilling)
                            if (payload.returnConsultation)
                                setReturnConsultation(
                                    payload.returnConsultation
                                )
                        }}
                    />
                    {/* Card: Meios de pagamento e parcelamento */}
                    <div className="card bg-base-100 shadow-sm border border-base-300">
                        <div className="card-body">
                            <div className="flex items-center gap-2 mb-1">
                                <MdPayments className="text-xl text-secondary" />
                                <h2 className="card-title text-lg">
                                    Meios de pagamento e parcelamento
                                </h2>
                            </div>
                            <p className="text-sm text-base-content/70 mb-4">
                                Define quais meios de pagamento ficam
                                disponíveis para o cliente e as regras de
                                parcelamento no cartão de crédito (aplicadas no
                                checkout).
                            </p>

                            {/* Meios de pagamento */}
                            <div className="mb-2">
                                <h3 className="font-semibold text-base-content mb-3">
                                    Meios de pagamento disponíveis
                                </h3>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                    {(
                                        [
                                            { key: 'pix', label: 'PIX' },
                                            {
                                                key: 'creditCard',
                                                label: 'Cartão de crédito',
                                            },
                                            {
                                                key: 'debitCard',
                                                label: 'Cartão de débito',
                                            },
                                        ] as const
                                    ).map(({ key, label }) => (
                                        <label
                                            key={key}
                                            className="flex items-center gap-3 p-4 rounded-xl border border-base-300 bg-base-50 cursor-pointer"
                                        >
                                            <input
                                                type="checkbox"
                                                className="toggle toggle-secondary toggle-sm"
                                                checked={payment.methods[key]}
                                                onChange={e =>
                                                    setPayment(prev => ({
                                                        ...prev,
                                                        methods: {
                                                            ...prev.methods,
                                                            [key]: e.target
                                                                .checked,
                                                        },
                                                    }))
                                                }
                                            />
                                            <span className="font-medium">
                                                {label}
                                            </span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {/* Parcelamento */}
                            <div className="mt-6 p-4 rounded-xl border border-base-300 bg-base-50">
                                <div className="flex items-center gap-2 mb-3">
                                    <MdCreditCard className="text-lg text-secondary" />
                                    <h3 className="font-semibold text-base-content">
                                        Parcelamento no cartão de crédito
                                    </h3>
                                </div>

                                <label className="flex items-center gap-3 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        className="checkbox checkbox-secondary"
                                        checked={payment.installmentsEnabled}
                                        onChange={e =>
                                            setPayment(prev => ({
                                                ...prev,
                                                installmentsEnabled:
                                                    e.target.checked,
                                            }))
                                        }
                                    />
                                    <span className="font-medium">
                                        Permitir parcelamento
                                    </span>
                                </label>

                                {payment.installmentsEnabled && (
                                    <div className="mt-5 space-y-5">
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <div className="form-control w-full">
                                                <label className="label">
                                                    <span className="label-text text-sm">
                                                        Máximo de parcelas
                                                    </span>
                                                </label>
                                                <select
                                                    className="select select-bordered select-sm w-full"
                                                    value={
                                                        payment.maxInstallments
                                                    }
                                                    onChange={e =>
                                                        setPayment(prev => ({
                                                            ...prev,
                                                            maxInstallments:
                                                                parseInt(
                                                                    e.target
                                                                        .value,
                                                                    10
                                                                ),
                                                        }))
                                                    }
                                                >
                                                    {Array.from(
                                                        {
                                                            length: MAX_INSTALLMENTS,
                                                        },
                                                        (_, i) => i + 1
                                                    ).map(n => (
                                                        <option
                                                            key={n}
                                                            value={n}
                                                        >
                                                            {n}x
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div className="form-control w-full">
                                                <label className="label">
                                                    <span className="label-text text-sm">
                                                        Valor mínimo por parcela
                                                    </span>
                                                </label>
                                                <input
                                                    type="text"
                                                    placeholder="R$ 0,00"
                                                    className="input input-bordered input-sm w-full"
                                                    value={
                                                        payment.minInstallmentValue
                                                    }
                                                    onChange={e =>
                                                        setPayment(prev => ({
                                                            ...prev,
                                                            minInstallmentValue:
                                                                currencyFormat(
                                                                    e.target
                                                                        .value
                                                                ),
                                                        }))
                                                    }
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <p className="text-sm font-medium text-base-content mb-1">
                                                Liberar parcelas a partir de um
                                                valor
                                            </p>
                                            <p className="text-xs text-base-content/60 mb-3">
                                                O valor mínimo vale para o
                                                número escolhido e para todos
                                                acima dele: definir R$ 250,00 em
                                                5x significa que abaixo desse
                                                valor a cobrança vai só até 4x.
                                                Deixe em branco para não exigir
                                                mínimo.
                                            </p>
                                            <div className="space-y-2">
                                                {Array.from(
                                                    {
                                                        length: Math.max(
                                                            0,
                                                            payment.maxInstallments -
                                                                1
                                                        ),
                                                    },
                                                    (_, i) => i + 2
                                                ).map(count => (
                                                    <div
                                                        key={count}
                                                        className="flex items-center gap-3"
                                                    >
                                                        <span className="text-sm font-medium w-12 shrink-0">
                                                            {count}x
                                                        </span>
                                                        <span className="text-sm text-base-content/60 shrink-0">
                                                            a partir de
                                                        </span>
                                                        <input
                                                            type="text"
                                                            placeholder="R$ 0,00"
                                                            className="input input-bordered input-sm w-40"
                                                            value={
                                                                payment.tiers[
                                                                    count
                                                                ] || ''
                                                            }
                                                            onChange={e =>
                                                                updateTier(
                                                                    count,
                                                                    e.target
                                                                        .value
                                                                )
                                                            }
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="card-actions justify-end mt-6">
                                <button
                                    onClick={handleSavePayment}
                                    disabled={savingPayment}
                                    className="btn btn-secondary gap-2"
                                >
                                    {savingPayment ? (
                                        <span className="loading loading-spinner loading-sm" />
                                    ) : (
                                        <MdSave className="text-lg" />
                                    )}
                                    Salvar
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Card: Cobrança pelo atendente virtual.
                        Só na saúde: as quatro opções falam de consulta, exame e
                        teleconsulta, e é ali que existe o "paga no dia, na
                        recepção". Nos demais segmentos a IA não agenda esses
                        serviços, e a opção seria uma pergunta sem resposta. */}
                    {isHealthSegment && (
                    <div className="card bg-base-100 shadow-sm border border-base-300 mt-6">
                        <div className="card-body">
                            <div className="flex items-center gap-2 mb-1">
                                <MdSmartToy className="text-xl text-secondary" />
                                <h2 className="card-title text-lg">
                                    Cobrança pelo atendente virtual
                                </h2>
                            </div>
                            <p className="text-sm text-base-content/70 mb-4">
                                O que a IA faz quando agenda uma consulta, exame,
                                procedimento ou teleconsulta pelo WhatsApp. Vale
                                só para o atendente virtual — o agendamento feito
                                pela equipe continua com a opção "Cobrar depois"
                                na própria tela.
                            </p>

                            <div className="flex flex-col gap-3">
                                {AI_BILLING_OPTIONS.map(option => (
                                    <label
                                        key={option.mode}
                                        className={`flex items-start gap-3 cursor-pointer rounded-xl border p-4 transition-colors ${
                                            aiBilling.mode === option.mode
                                                ? 'border-secondary bg-secondary/5'
                                                : 'border-base-300 hover:bg-base-200/40'
                                        }`}
                                    >
                                        <input
                                            type="radio"
                                            name="ai-billing-mode"
                                            className="radio radio-secondary mt-0.5"
                                            checked={aiBilling.mode === option.mode}
                                            onChange={() =>
                                                setAiBilling({ mode: option.mode })
                                            }
                                        />
                                        <span>
                                            <span className="font-medium block">
                                                {option.label}
                                            </span>
                                            <span className="text-sm text-base-content/60">
                                                {option.description}
                                            </span>
                                        </span>
                                    </label>
                                ))}
                            </div>

                            <div className="card-actions justify-end mt-6">
                                <button
                                    onClick={handleSaveAiBilling}
                                    disabled={savingAiBilling}
                                    className="btn btn-secondary gap-2"
                                >
                                    {savingAiBilling ? (
                                        <span className="loading loading-spinner loading-sm" />
                                    ) : (
                                        <MdSave className="text-lg" />
                                    )}
                                    Salvar
                                </button>
                            </div>
                        </div>
                    </div>
                    )}

                    {/* Card: Cobrança da consulta de retorno.
                        Só na saúde que atende o próprio paciente: a intermediação
                        revende o atendimento de um parceiro e cobra pelo
                        encaminhamento — ali não existe "volta para mostrar o
                        exame", e a consulta de retorno nem é marcada. */}
                    {showReturnConsultation && (
                    <div className="card bg-base-100 shadow-sm border border-base-300 mt-6">
                        <div className="card-body">
                            <div className="flex items-center gap-2 mb-1">
                                <MdEventRepeat className="text-xl text-secondary" />
                                <h2 className="card-title text-lg">
                                    Cobrança da consulta de retorno
                                </h2>
                            </div>
                            <p className="text-sm text-base-content/70 mb-4">
                                Vale para a consulta marcada como "retorno" na
                                tela de agendamento. Na maioria dos consultórios
                                o retorno é a continuação da consulta já paga —
                                o paciente volta para mostrar o exame ou ouvir o
                                resultado — e nada é cobrado por ele.
                            </p>

                            <label className="flex items-start gap-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    className="checkbox checkbox-secondary mt-0.5"
                                    checked={returnConsultation.chargeReturn}
                                    onChange={e =>
                                        setReturnConsultation({
                                            chargeReturn: e.target.checked,
                                        })
                                    }
                                />
                                <span>
                                    <span className="font-medium block">
                                        Cobrar consulta de retorno
                                    </span>
                                    <span className="text-sm text-base-content/60">
                                        Desligado (padrão), marcar a consulta
                                        como retorno agenda sem emitir cobrança:
                                        o valor fica guardado no atendimento e a
                                        emissão sai depois, pelo "Gerar
                                        cobrança" da tela do atendimento. Ligado,
                                        o retorno é cobrado como qualquer outra
                                        consulta.
                                    </span>
                                </span>
                            </label>

                            <div className="card-actions justify-end mt-6">
                                <button
                                    onClick={handleSaveReturnConsultation}
                                    disabled={savingReturnConsultation}
                                    className="btn btn-secondary gap-2"
                                >
                                    {savingReturnConsultation ? (
                                        <span className="loading loading-spinner loading-sm" />
                                    ) : (
                                        <MdSave className="text-lg" />
                                    )}
                                    Salvar
                                </button>
                            </div>
                        </div>
                    </div>
                    )}
                </>
            )}
        </div>
    )
}
